/**
 * Banner pricing calculation module.
 * Mirrors the backend BANNER_PRICING_CONFIG model — prices are fetched from
 * /api/configurators/banners and these defaults are used as a fallback.
 */

/* ── Hardcoded fallback (kept in sync with backend) ── */

const FALLBACK_CONFIG = {
  materials: [
    { id: "pvc-440", label: "PVC 440gsm", price_per_sqm: 22.0, short: "All-round indoor / outdoor" },
    { id: "pvc-550", label: "PVC 550gsm", price_per_sqm: 28.0, short: "Heavy-duty outdoor" },
    { id: "mesh", label: "Mesh", price_per_sqm: 26.0, short: "Wind-resistant, perforated" },
    { id: "fabric", label: "Fabric", price_per_sqm: 30.0, short: "Indoor / roll-up display" },
  ],
  eyelets: [
    { id: "none", label: "No eyelets", cost_per_eyelet: 0, spacing_m: 0 },
    { id: "standard", label: "Standard eyelets", cost_per_eyelet: 0.5, spacing_m: 0.5 },
    { id: "corner", label: "Corner eyelets only", flat_cost: 3.0, spacing_m: 0 },
    { id: "heavy", label: "Heavy-duty eyelets", cost_per_eyelet: 0.8, spacing_m: 0.4 },
  ],
  reinforcements: [
    { id: "none", label: "No reinforcement", cost_per_m: 0 },
    { id: "hem", label: "Hemmed edges", cost_per_m: 0 },
    { id: "double-hem", label: "Double hem", cost_per_m: 2.5 },
    { id: "webbing", label: "Webbing reinforcement", cost_per_m: 4.0 },
    { id: "pole-pocket", label: "Pole pocket (top + bottom)", flat_cost_each: 8.0, count: 2 },
  ],
  sizes: [
    { label: "1m x 0.5m", w_m: 1.0, h_m: 0.5, note: "Small shop / counter" },
    { label: "2m x 0.5m", w_m: 2.0, h_m: 0.5, note: "Retail promo / fence" },
    { label: "2m x 1m", w_m: 2.0, h_m: 1.0, note: "Roll-up / indoor display" },
    { label: "3m x 1m", w_m: 3.0, h_m: 1.0, note: "Event / frontage" },
    { label: "4m x 1m", w_m: 4.0, h_m: 1.0, note: "Large outdoor" },
    { label: "4m x 1.5m", w_m: 4.0, h_m: 1.5, note: "Scaffold / site wrap" },
    { label: "6m x 1.5m", w_m: 6.0, h_m: 1.5, note: "Large scaffold wrap" },
  ],
  minimum_order_gbp: 20.0,
  double_sided_multiplier: 1.6,
  bulk_discounts: [
    { min_qty: 1, max_qty: 4, discount: 0 },
    { min_qty: 5, max_qty: 9, discount: 0.05 },
    { min_qty: 10, max_qty: 24, discount: 0.1 },
    { min_qty: 25, max_qty: 99999, discount: 0.15 },
  ],
};

/* ── Fetch live config (with fallback) ── */

let _cachedConfig = null;

export async function fetchBannerConfig() {
  if (_cachedConfig) return _cachedConfig;
  try {
    const base = "";
    const res = await fetch(`${base}/api/configurators/banners`);
    if (!res.ok) throw new Error(res.statusText);
    _cachedConfig = await res.json();
    return _cachedConfig;
  } catch (err) {
    console.error("[bannerPricing] fetch config failed, using fallback:", err);
    _cachedConfig = FALLBACK_CONFIG;
    return _cachedConfig;
  }
}

export function getFallbackConfig() {
  return FALLBACK_CONFIG;
}

/* ── Price calculation ── */

/**
 * @param {object} opts
 * @param {number} opts.widthM   - width in metres
 * @param {number} opts.heightM  - height in metres
 * @param {string} opts.materialId - e.g. "pvc-440"
 * @param {string} opts.eyeletId   - e.g. "standard", "none"
 * @param {string} opts.reinforcementId - e.g. "webbing", "none"
 * @param {boolean} opts.doubleSided
 * @param {number} opts.quantity
 * @param {object} config - pricing config (from API or fallback)
 * @returns {{ unitPrice: number, totalPrice: number, bulkDiscount: number, breakdown: object }}
 */
export function calculateBannerPrice({
  widthM,
  heightM,
  materialId = "pvc-440",
  eyeletId = "none",
  reinforcementId = "none",
  doubleSided = false,
  quantity = 1,
}, config = FALLBACK_CONFIG) {
  const area = widthM * heightM;
  const perimeter = 2 * (widthM + heightM);

  // Material cost
  const mat = config.materials.find((m) => m.id === materialId) || config.materials[0];
  let baseCost = area * mat.price_per_sqm;

  // Double-sided
  if (doubleSided) {
    baseCost *= config.double_sided_multiplier || 1.6;
  }

  // Eyelet cost
  const eyelet = config.eyelets.find((e) => e.id === eyeletId) || config.eyelets[0];
  let eyeletCost = 0;
  if (eyelet.flat_cost) {
    eyeletCost = eyelet.flat_cost;
  } else if (eyelet.spacing_m > 0 && eyelet.cost_per_eyelet > 0) {
    const count = Math.max(4, Math.ceil(perimeter / eyelet.spacing_m));
    eyeletCost = count * eyelet.cost_per_eyelet;
  }

  // Reinforcement cost
  const reinf = config.reinforcements.find((r) => r.id === reinforcementId) || config.reinforcements[0];
  let reinforcementCost = 0;
  if (reinf.flat_cost_each) {
    reinforcementCost = reinf.flat_cost_each * (reinf.count || 2);
  } else if (reinf.cost_per_m) {
    reinforcementCost = perimeter * reinf.cost_per_m;
  }

  // Unit price (before bulk)
  let unitPrice = baseCost + eyeletCost + reinforcementCost;

  // Enforce minimum
  const min = config.minimum_order_gbp || 20;
  if (unitPrice < min) unitPrice = min;

  // Bulk discount
  const qty = Math.max(1, quantity);
  const tier = (config.bulk_discounts || []).find(
    (t) => qty >= t.min_qty && qty <= t.max_qty
  );
  const discount = tier ? tier.discount : 0;
  const discountedUnit = unitPrice * (1 - discount);
  const totalPrice = discountedUnit * qty;

  return {
    unitPrice: Math.round(discountedUnit * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100,
    bulkDiscount: discount,
    breakdown: {
      area,
      perimeter,
      materialCost: Math.round(baseCost * 100) / 100,
      eyeletCost: Math.round(eyeletCost * 100) / 100,
      reinforcementCost: Math.round(reinforcementCost * 100) / 100,
    },
  };
}

/* ── Formatting helper ── */

export function formatGBP(amount) {
  return `\u00a3${amount.toFixed(2)}`;
}
